import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Car, Activity, Users, Wallet } from "lucide-react";

const RIDE_STATUS = [
  { value: "requested", label: "Tilattu" },
  { value: "assigned", label: "Myönnetty" },
  { value: "in_progress", label: "Menossa" },
  { value: "completed", label: "Valmis" },
  { value: "cancelled", label: "Peruutettu" },
];
const PAYMENT_STATUS = [
  { value: "pending", label: "Odottaa" },
  { value: "paid", label: "Maksettu" },
  { value: "refunded", label: "Palautettu" },
];
const PAYMENT_METHODS = [
  { value: "card", label: "Kortti" },
  { value: "cash", label: "Käteinen" },
  { value: "invoice", label: "Lasku" },
];
const RIDE_TYPES = [
  { value: "standard", label: "Vakio" },
  { value: "premium", label: "Premium" },
  { value: "shared", label: "Jaettu" },
  { value: "airport", label: "Lentokenttä" },
];
const DRIVER_STATUS = [
  { value: "active", label: "Aktiivinen" },
  { value: "off_duty", label: "Vapaalla" },
  { value: "inactive", label: "Ei käytössä" },
];

export default function TaxiDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/taxi/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const rides = {
    endpoint: "/taxi/rides",
    testidPrefix: "rides",
    emptyMessage: "Ei kyytejä",
    defaults: { status: "requested", payment_status: "pending", payment_method: "card", ride_type: "standard", distance_km: 0, duration_min: 0, base_fare: 0, distance_fare: 0, waiting_fare: 0 },
    fields: [
      { key: "customer_name", label: "Asiakas", type: "text" },
      { key: "customer_phone", label: "Puhelin", type: "text" },
      { key: "pickup_address", label: "Lähtöosoite", type: "text", span: 2 },
      { key: "dropoff_address", label: "Pääteosoite", type: "text", span: 2 },
      { key: "pickup_time", label: "Lähtöaika", type: "text", placeholder: "2026-06-16 14:00" },
      { key: "dropoff_time", label: "Saapumisaika", type: "text" },
      { key: "driver_name", label: "Kuljettaja", type: "text" },
      { key: "vehicle_plate", label: "Auton rek.nro", type: "text" },
      { key: "distance_km", label: "Matka km", type: "number" },
      { key: "duration_min", label: "Kesto min", type: "number" },
      { key: "base_fare", label: "Perusmaksu €", type: "number" },
      { key: "distance_fare", label: "Matkamaksu €", type: "number" },
      { key: "waiting_fare", label: "Odotusmaksu €", type: "number" },
      { key: "ride_type", label: "Kyydin tyyppi", type: "select", options: RIDE_TYPES },
      { key: "status", label: "Tila", type: "select", options: RIDE_STATUS },
      { key: "payment_method", label: "Maksutapa", type: "select", options: PAYMENT_METHODS },
      { key: "payment_status", label: "Maksun tila", type: "select", options: PAYMENT_STATUS },
      { key: "rating", label: "Arvio (0-5)", type: "number", step: "0.1" },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "ride_number", label: "Nro" },
      { key: "customer_name", label: "Asiakas" },
      { key: "driver_name", label: "Kuljettaja" },
      { key: "vehicle_plate", label: "Auto" },
      { key: "distance_km", label: "Km", align: "right" },
      { key: "total_fare", label: "Hinta", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  const drivers = {
    endpoint: "/taxi/drivers",
    testidPrefix: "taxi-drivers",
    emptyMessage: "Ei kuljettajia",
    defaults: { name: "", status: "active", rating: 0, total_rides: 0 },
    fields: [
      { key: "name", label: "Nimi", type: "text", required: true },
      { key: "phone", label: "Puhelin", type: "text" },
      { key: "license_number", label: "Ajokorttinumero", type: "text" },
      { key: "taxi_license", label: "Taksilupa", type: "text" },
      { key: "vehicle_plate", label: "Auton rek.nro", type: "text" },
      { key: "status", label: "Tila", type: "select", options: DRIVER_STATUS },
      { key: "rating", label: "Arvio (0-5)", type: "number", step: "0.1" },
      { key: "total_rides", label: "Kyytejä yht.", type: "number", step: "1" },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "name", label: "Nimi" },
      { key: "phone", label: "Puhelin" },
      { key: "vehicle_plate", label: "Auto" },
      { key: "taxi_license", label: "Taksilupa" },
      { key: "rating", label: "Arvio", format: "num", digits: 1, align: "right" },
      { key: "total_rides", label: "Kyydit", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="taxi-dashboard">
      <PageHeader kicker="Sovellus" title="Taksi" description="Kyydit ja kuljettajat." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Users className="w-5 h-5" />} label="Aktiiviset kuljettajat" value={stats?.drivers_active ?? 0} accent="text-emerald-300" loading={loading} testid="taxi-kpi-drivers" />
        <KpiCard icon={<Car className="w-5 h-5" />} label="Kyydit tänään" value={stats?.rides_today ?? 0} loading={loading} testid="taxi-kpi-today" />
        <KpiCard icon={<Activity className="w-5 h-5" />} label="Käynnissä" value={stats?.rides_active ?? 0} accent="text-blue-300" loading={loading} testid="taxi-kpi-active" />
        <KpiCard icon={<Wallet className="w-5 h-5" />} label="LV tänään" value={formatEur(stats?.revenue_today)} accent="text-amber-300" loading={loading} testid="taxi-kpi-rev" />
      </div>
      <Panel title="Hallinta">
        <Tabs defaultValue="rides" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="rides" data-testid="taxi-tab-rides">Kyydit</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="taxi-tab-drivers">Kuljettajat</TabsTrigger>
            <TabsTrigger value="staff" data-testid="taxi-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="rides"><CrudList config={rides} /></TabsContent>
          <TabsContent value="drivers"><CrudList config={drivers} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="taxi" moduleLabel="Taksi" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
